<script setup lang="ts">
import { computed } from 'vue'
interface Props {
  src?: string
  fallback?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'
  status?: 'online' | 'offline' | 'busy'
  shape?: 'circle' | 'square'
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  shape: 'circle',
})

const sizeClasses = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-sm',
  md: 'w-10 h-10 text-base',
  lg: 'w-12 h-12 text-lg',
  xl: 'w-16 h-16 text-xl',
  // Fills whatever parent container it's placed in; use when the parent
  // already defines the dimensions (e.g. the rotated profile avatar frame).
  full: 'w-full h-full text-2xl',
}

const statusSizes = {
  xs: 'w-1.5 h-1.5',
  sm: 'w-2 h-2',
  md: 'w-2.5 h-2.5',
  lg: 'w-3 h-3',
  xl: 'w-4 h-4',
}

const statusColors = {
  online: 'bg-green-500',
  offline: 'bg-gray-500',
  busy: 'bg-yellow-500',
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const hasImage = computed(() => !!props.src && props.src !== '')

const initials = computed(() => (props.fallback ? getInitials(props.fallback) : '?'))

const avatarClasses = computed(() => [
  'inline-flex items-center justify-center bg-surface-2 border border-line-2 overflow-hidden',
  props.shape === 'circle' ? 'rounded-full' : 'rounded-xl',
  sizeClasses[props.size],
])

const statusClasses = computed(() => [
  'absolute bottom-0 right-0 border-2 border-canvas dark:border-black',
  props.shape === 'circle' ? 'rounded-full' : 'rounded-br-xl',
  statusSizes[props.size],
  props.status ? statusColors[props.status] : '',
])
</script>

<template>
  <div class="relative inline-flex shrink-0">
    <div :class="avatarClasses">
      <img
        v-if="hasImage"
        :src="props.src"
        :alt="props.fallback || 'Avatar'"
        class="w-full h-full object-cover"
        loading="lazy"
      />
      <span v-else class="font-medium text-ink-2 select-none">
        {{ initials }}
      </span>
    </div>

    <span v-if="props.status" :class="statusClasses" :aria-label="props.status" />
  </div>
</template>